import { Injectable, ElementRef, Renderer2, RendererFactory2, EventEmitter } from '@angular/core';
import { LinkGroupConfig } from '../models/link-group-config.model';
import { Coordinates } from 'src/app/reports/components/report/models/coordinates.model';
import { LinkGroup } from '../models/link-group.model';
import { Link } from '../models/link.model';
import { LinkElement } from '../models/link-element.model';
import { BeforeRegisterLink } from '../models/before-register-link.model';
import { AfterRegisterLink } from '../models/after-register-link.model';
import { SelectedShape } from '../models/selected-shape.model';
import { EditLink } from '../models/edit-link.model';
import { BeforeSelectElementOnEditLink } from '../models/before-select-element-on-edit-link.model';
import { isLeftClickHounding } from 'src/app/reports/helpers/element.helper';
import { adjustCoordenateOfLinkElement, compareLinkElements, createSelectedShapeInstance, getBeforeSelectElementOnEditLinkInstance, getEditLinkInstance, getVirtualElementByData, getAfterRegisterLinkInstance, getLinkInstance } from '../helpers/link.helper';
import { createSVG, drawCircle, drawCurvedLine, updateCurvedLineByCircle } from '../helpers/elements-svg.helper';
import { getCoordinateFromElement, getCoordinateFromMouseEvent, isClosestElement } from '../helpers/element-position.helper';
import { isElementValid, removeElement } from '../helpers/element-utils.helper';

@Injectable({
  providedIn: 'root'
})
export class LinkService {

  //#region Properties
  private renderer: Renderer2;

  groups: Array<LinkGroup> = new Array<LinkGroup>();
  aditionFactor = 10;

  beforeRegisterLink = new EventEmitter<BeforeRegisterLink>();
  afterRegisterLink = new EventEmitter<AfterRegisterLink>();

  beforeRemoveTarget = new EventEmitter<LinkElement>();
  afterRegisterTarget = new EventEmitter<LinkElement>();
  beforeSelectElementOnEditLink = new EventEmitter<BeforeSelectElementOnEditLink>();
  onEditLink = new EventEmitter<EditLink>();
  onLinkSelected = new EventEmitter<SelectedShape>();
  onCircleSelected = new EventEmitter<SelectedShape>();

  //#endregion

  //#region Ctor

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  //#endregion

  //#region Group Methods

  setGroup(name: string, config: LinkGroupConfig, scale: number = 1) {
    if (!this.getGroupByName(name)) {
      this.groups.push({
        name: name,
        config: config,
        elements: [],
        links: [],
        targets: [],
        scale: scale
      });
    }
  }

  removeGroup(name: string) {
    const obj = this.groups.find(value => {
      return value.name === name;
    });

    if (obj) {
      const index = this.groups.indexOf(obj);
      this.checkElements(name, true);
      this.groups.splice(index);
    }
  }

  setContentRefOnGroup(name: string, elm: ElementRef) {
    const group = this.getGroupByName(name);
    if (group) {
      group.contentRef = elm;

      this.setMouseEventToContent(group);
    }
  }

  getGroupByName(name) {
    return this.groups.find(value => value.name == name);
  }

  updateScaleNumberOfGroup(newScale: number, groupName: string) {
    const group = this.getGroupByName(groupName);
    if (group) {
      group.scale = newScale;
    }
  }

  setDisabledToGroup(name: string | LinkGroup, action: boolean) {
    const group = this.getGroupByName(name);

    if (group) {
      group.disabled = action;
    }
  }

  //#endregion

  //#region Data Control

  registerVirtualLinkByData(dataFisrtElm: any, dataSecondElm: any, groupName: string) {
    const group = this.getGroupByName(groupName);

    const link: Link = getLinkInstance(dataFisrtElm, group, dataSecondElm);

    if (link && link.first && link.second) {

      this.connectElements(link, group);
      group.links.push(link);

      this.afterRegisterLink.emit(getAfterRegisterLinkInstance(link, group));
    }
  }

  registerVirtualLinkElement(link: LinkElement, group: LinkGroup): LinkElement {
    group.elements = group.elements ? group.elements : [];

    const elments = group.elements;

    if (elments.indexOf(link) === -1) {
      elments.push(link);
    }

    return link;
  }

  registerLink(link: LinkElement, group: LinkGroup) {

    if (link.targetIsActive) {
      this.removeTarget(link, group);
      return;
    }

    if (this.isTargetRegistred(link, group)) {
      return;
    }

    const registerLink = this.getNewBeforeRegisterLink(link, group);
    this.beforeRegisterLink.emit(registerLink);

    if (registerLink.removeFisrtTarget) {
      this.removeTarget(group.targets[0], group);
    }

    if (!registerLink.cancel) {
      this.registerTarget(link, group);
    }

    if (group.targets.length == 2) {
      const link: Link = {
        first: group.targets[0],
        second: group.targets[1]
      };

      this.connectElements(link, group);
      group.links.push(link);

      this.afterRegisterLink.emit({
        currentLink: link,
        links: group.links
      });

      for (let index = (group.targets.length - 1); index >= 0; index--) {
        const element = group.targets[index];
        this.removeTarget(element, group);
      }
    }
  }

  isTargetRegistred(link: LinkElement, group: LinkGroup): boolean {
    let isRegistred = false;

    if (group.targets.length == 1) {
      const firstTarget = group.targets[0];

      const find = group.links.find(element => compareLinkElements(link, firstTarget, element));

      isRegistred = !!find;
    }

    return isRegistred;
  }

  registerTarget(link: LinkElement, group: LinkGroup) {
    if (group.targets.indexOf(link) === -1) {
      group.targets.push(link);
      link.targetIsActive = true;
      this.afterRegisterTarget.emit(link);
    }
  }

  removeTarget(link: LinkElement, group: LinkGroup) {
    const index = group.targets.indexOf(link);
    if (index !== -1) {

      this.beforeRemoveTarget.emit(link);

      group.targets.splice(index, 1);
      link.targetIsActive = false;
    }
  }

  removeAllTargets(group: LinkGroup) {
    const aux = group.targets.concat();

    aux.forEach(value => this.removeTarget(value, group));
  }

  getNewBeforeRegisterLink(link: LinkElement, group: LinkGroup): BeforeRegisterLink {
    return {
      removeFisrtTarget: false,
      cancel: false,
      data : link.data,
      newElement : link.element,
      oldElement : group.targets.length == 0 ? null : group.targets[0].element
    };
  }

  getElementFromLinkElement(link: LinkElement) {
    return link ? link.element : null;
  }

  updateCoordenates(groupName: string) {
    const group: LinkGroup = this.getGroupByName(groupName);
    if (group) {
      group.links.forEach(value => this.connectElements(value, group));
    }
  }

  checkElements(groupName: string, force: boolean = false) {
    const group: LinkGroup = this.getGroupByName(groupName),
        aux = [];

    if (group) {
      group.links.forEach(value => {
        if (!isElementValid(value.first.element) || !isElementValid(value.second.element) || force) {
          aux.push(value);
        }
      });
    }

    aux.forEach(value => this.removeLink(value, group));
  }

  removeLink(link: Link, group: LinkGroup) {
    const index = group.links.indexOf(link);
    if (index !== -1) {

      // remove elements from DOM
      removeElement(link.secondCircle);
      removeElement(link.firstCircle);
      removeElement(link.lineShape);

      group.links.splice(index, 1);
    }
  }

  getLinksByGroup(groupName: string) {
    const group: LinkGroup = this.getGroupByName(groupName);

    return group.links;
  }

  connectElements(link: Link, group: LinkGroup) {
    const first = getCoordinateFromElement(link.first.element, group);
    const second = getCoordinateFromElement(link.second.element, group);

    if (link.first.coordinates != first || link.second.coordinates != second) {

      link.first.coordinates = first;
      link.second.coordinates = second;

      adjustCoordenateOfLinkElement(link.second, link.first, group, this.aditionFactor);
      adjustCoordenateOfLinkElement(link.first, link.second, group, this.aditionFactor);

      const isNew = !isElementValid(link.lineShape);

      link.firstCircle = drawCircle(link.first.coordinates, group, link.firstCircle, group.config.primaryColor);
      link.secondCircle = drawCircle(link.second.coordinates, group, link.secondCircle, group.config.primaryColor);

      link.lineShape = drawCurvedLine(link.first.coordinates, link.second.coordinates, group, link.lineShape);

      if (isNew) {
        this.setMouseEvents(group, link);
      }
    }
  }

  //#endregion

  //#region Events

  setMouseEvents(group: LinkGroup, link: Link) {

    this.renderer.listen(link.firstCircle, 'mousedown', (event) => {
      this.circleSelected(link, link.first, group, event);
    });

    this.renderer.listen(link.secondCircle, 'mousedown', (event) => {
      this.circleSelected(link, link.second, group, event);
    });

    this.renderer.listen(link.lineShape, 'click', (event) => {
      this.linkSelected(link, group, event);
    });
  }

  //#region Edition

  circleSelected(link: Link, linkElement: LinkElement, group: LinkGroup, event: MouseEvent) {
    group.selectedCircle = createSelectedShapeInstance(link, event, linkElement);

    this.onCircleSelected.emit(group.selectedCircle);
  }

  setMouseEventToContent(group: LinkGroup) {
    this.renderer.listen(group.contentRef, 'mousemove', (event: MouseEvent) => {

      const selected = group.selectedCircle;

      if (selected && !group.disabled) {
        const coordinates = getCoordinateFromMouseEvent(event, group.contentRef, group.scale);

        if (!isLeftClickHounding(event)) {

          if (group.closestElement) {
            this.editLink(selected, group);
          }

          setTimeout(() => this.updateCoordenates(group.name));

          group.selectedCircle = null;
        } else {
          this.selectElementToEdition(coordinates, selected, group);
        }
      }
    });
  }

  editLink(selected: SelectedShape , group: LinkGroup) {
    const oldElement = selected.linkElement,
      newLinkElement = group.closestElement;

    if (selected.link.first == oldElement) {
      selected.link.first = newLinkElement;
    } else {
      selected.link.second = newLinkElement;
    }

    const onEdit = getEditLinkInstance(group, selected, newLinkElement, oldElement);
    this.onEditLink.emit(onEdit);
  }

  selectElementToEdition(coordinates: Coordinates, selected: SelectedShape , group: LinkGroup) {
    const oldElm = group.closestElement,
        newElm = this.getClosestElementByGroup(coordinates, group.closestElement, group);

    const before = getBeforeSelectElementOnEditLinkInstance(group, selected, newElm, oldElm);

    this.beforeSelectElementOnEditLink.emit(before);

    group.closestElement = !before.cancel ? newElm : null;
    updateCurvedLineByCircle(coordinates, selected.shape, selected.link, group, this.aditionFactor);
    drawCircle(coordinates, group, selected.shape);
  }

  getClosestElementByGroup(coordinates: Coordinates, oldElement: LinkElement, group: LinkGroup): LinkElement {
    let element: LinkElement = oldElement;

    if (element == null) {
       element = group.elements.find(value => isClosestElement(coordinates, value, group));
    } else
      if (!isClosestElement(coordinates, element, group)) {
        return this.getClosestElementByGroup(coordinates, null, group);
      }

    return element;
  }

  //#endregion

  //#region Selection

  linkSelected(link: Link, group: LinkGroup, event: MouseEvent) {
    const target: any = event.target;
    let selected: SelectedShape = null;

    if (!target.getAttribute('filter')) {
      selected = createSelectedShapeInstance(link, event);
    }

    this.onLinkSelected.emit(selected);
  }

  //#endregion

  //#endregion

  //#region SVG

  creteSVGOnGroup(name: string) {
    const group = this.getGroupByName(name);
    if (group && !group.svgRef) {
      group.svgRef = createSVG(group.contentRef);
    }
  }

  //#endregion
}
